import { useArtifactGate, GateLock } from './ArtifactGate';
import React, { useState } from 'react';
import { FieldCoordinates } from '../types/codeGeneration';

interface GenerationControlsProps {
  fields: FieldCoordinates[];
  onGenerateFillablePDF: (formData: any) => void;
  onGenerateFlattenedPDF: (formData: any) => void;
  onGenerateSpringBootProject: (formData: any) => void;
  onOpenLivePreview?: () => void;
}

const GenerationControls: React.FC<GenerationControlsProps> = ({
  fields,
  onGenerateFillablePDF,
  onGenerateFlattenedPDF,
  onGenerateSpringBootProject,
  onOpenLivePreview
}) => {
  const { guard, allows } = useArtifactGate();
  const [isGenerating, setIsGenerating] = useState(false);

  if (fields.length === 0) {
    return null;
  }

  const handleGenerate = async (generateFn: (formData: any) => void) => {
    setIsGenerating(true);
    try {
      await generateFn(fields);
    } catch (error) {
      console.error('Generation failed:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-soft-lg border border-neutral-200 overflow-hidden">
      <div className="p-6 border-b border-neutral-200 bg-gradient-to-r from-emerald-50 to-emerald-100/50">
        <div className="text-center">
          <h3 className="text-lg font-display font-semibold text-neutral-900 mb-2">Generate Output</h3>
          <p className="text-sm text-neutral-600">
            {fields.length} field{fields.length !== 1 ? 's' : ''} configured - choose your output format
          </p>
        </div>
      </div>
      
      <div className="p-6">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Spring Boot Project Section */}
          <div className="border border-emerald-200 rounded-xl p-4 bg-emerald-50">
            <h4 className="text-base font-semibold text-neutral-900 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
              Application Project
            </h4>
            <button
              onClick={() => guard('project', () => handleGenerate(onGenerateSpringBootProject))}
              disabled={isGenerating}
              className="w-full flex items-center justify-center px-6 py-4 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-lg font-semibold hover:from-emerald-700 hover:to-emerald-800 transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? (
                <svg className="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              )}
              Generate Spring Boot Project
              <GateLock show={!allows('project')} />
            </button>
            <div className="text-xs text-neutral-500 mt-2 space-y-1">
              <p>Generate a turn-key Spring Boot application ready to populate PDFs</p>
              <p>Custom entities, REST API, Database schema, and Docker images per PDF</p>
              <p>PDF specific Docker images for Docker Compose, Kubernetes, or Nomad deployment</p>
              <p>Enterprise customers also receive Spring Boot project source code</p>
            </div>
          </div>

          {/* Live Form Preview Section */}
          <div className="border border-blue-200 rounded-xl p-4 bg-blue-50">
            <h4 className="text-base font-semibold text-neutral-900 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Fill &amp; Generate PDFs
            </h4>
            <button
              onClick={onOpenLivePreview}
              className="w-full flex items-center justify-center px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-lg"
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Open Live Form Preview
            </button>
            <div className="text-xs text-neutral-500 mt-2 space-y-1">
              <p>Fill in form fields with actual values, then generate output PDFs</p>
              <p>Generate Fillable PDFs with interactive fields for end users</p>
              <p>Generate Flattened PDFs with values permanently baked in</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerationControls;